import { ScrollPicker, PickerValue } from "@/components/scroll-picker";
import {
  AmendSheetShell,
  ConfirmChangeButton,
} from "@/features/onboarding/components/amend-sheet-shell";
import { useGetCalories } from "@/hooks/requests/nutritional-requirement/use-get-calories";
import { useUpdateCalories } from "@/hooks/requests/nutritional-requirement/use-update-calories";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { useEffect, useMemo, useState } from "react";

interface AmendCaloriesSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const SHEET_HEIGHT_PX = 362;

const MIN_CALORIES = 500;
const MAX_CALORIES = 5000;
const CALORIE_STEP = 50;

const FEMALE_MIN_CALORIES = 1200;
const FEMALE_MAX_CALORIES = 2400;
const MALE_MIN_CALORIES = 1600;
const MALE_MAX_CALORIES = 3000;

const calorieOptions: string[] = (() => {
  const options: string[] = [];
  for (let i = MIN_CALORIES; i <= MAX_CALORIES; i += CALORIE_STEP) {
    options.push(String(i));
  }
  return options;
})();

const renderItem = (value: string, selected: boolean) => (
  <div
    className={cn(
      "flex h-[56px] w-full items-center justify-center text-[22px] font-semibold",
      selected ? "text-[#43246e]" : "text-[#7485a7]",
    )}
  >
    {value}
  </div>
);

/**
 * "Daily Calorie Target" bottom sheet (Figma 983:5848). Ported from the old
 * EditDailyCaloriesModal — same picker, seeding, and sex-dependent bounds —
 * with the container swapped from a centered modal to the v5 action sheet.
 */
export const AmendCaloriesSheet = ({ isOpen, onDismiss }: AmendCaloriesSheetProps) => {
  const { showError } = useToast();
  const { user } = useGetUser();
  const { calories, isLoading: isCaloriesLoading } = useGetCalories();
  const { updateCaloriesAsync, isUpdating: isUpdatingCalories } = useUpdateCalories();

  const currentCalories = useMemo(
    () => parseInt(calories?.dailyCalories?.toString() ?? String(MIN_CALORIES)) || MIN_CALORIES,
    [calories],
  );

  const [pickerValue, setPickerValue] = useState<PickerValue>(() => {
    const nearest =
      calorieOptions.find((opt) => Number(opt) >= currentCalories) ?? String(MIN_CALORIES);
    return { calories: nearest };
  });

  // Re-seed from the server value whenever it changes, and on reopen — the
  // sheet must discard edits abandoned via backdrop dismiss.
  useEffect(() => {
    if (!isOpen || !currentCalories) return;
    const nearest =
      calorieOptions.find((opt) => Number(opt) >= currentCalories) ?? String(MIN_CALORIES);
    setPickerValue({ calories: nearest });
  }, [isOpen, currentCalories]);

  const handleConfirm = async () => {
    const selectedCalories = Number(pickerValue.calories);
    const userSex = user?.personalInfo?.sex?.toLowerCase();

    if (userSex === "female") {
      if (selectedCalories < FEMALE_MIN_CALORIES) {
        showError(`The minimum daily calorie target for females is ${FEMALE_MIN_CALORIES} kcal`);
        return;
      }
      if (selectedCalories > FEMALE_MAX_CALORIES) {
        showError(`The maximum daily calorie target for females is ${FEMALE_MAX_CALORIES} kcal`);
        return;
      }
    } else if (userSex === "male") {
      if (selectedCalories < MALE_MIN_CALORIES) {
        showError(`The minimum daily calorie target for males is ${MALE_MIN_CALORIES} kcal`);
        return;
      }
      if (selectedCalories > MALE_MAX_CALORIES) {
        showError(`The maximum daily calorie target for males is ${MALE_MAX_CALORIES} kcal`);
        return;
      }
    }

    try {
      await updateCaloriesAsync(
        { dailyCalories: selectedCalories },
        {
          onSuccess: () => onDismiss(),
          onError: (error) => showError(error.message),
        },
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <AmendSheetShell isOpen={isOpen} onDismiss={onDismiss} contentHeightPx={SHEET_HEIGHT_PX}>
      <div className="flex w-full flex-col items-center gap-4">
        <h3 className="text-center text-[18px] font-bold text-black">
          Daily Calorie Target (kcal)
        </h3>

        <div className="w-full max-w-[322px]">
          <ScrollPicker value={pickerValue} onChange={setPickerValue} height={168} itemHeight={56}>
            <ScrollPicker.Column name="calories" options={calorieOptions} renderItem={renderItem} />
          </ScrollPicker>
        </div>

        <ConfirmChangeButton
          onClick={handleConfirm}
          disabled={isCaloriesLoading}
          isSaving={isUpdatingCalories}
        />
      </div>
    </AmendSheetShell>
  );
};
